import React, {useState, useEffect, useCallback, useRef, useMemo} from "react";
import ENDPOINTS from '../config/apiEndPoints';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/accessibility';
import LoadingSpinner from "./LoadingSpinner";
import {getUrlParam, openPopup} from "../config/windowUtils";
import MenuButton from "./MenuButtons";
import {shareComponent, ShareModal} from "../config/shareUtils";
import ActionButtons from "./ActionButtons";
import AutoComplete from "./AutoComplete";

const LOCAL_BODY_TYPES = [
  { value: 'gramapanchayat', label: 'Grama Panchayat' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'jillapanchayat', label: 'District Panchayat' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'blockpanchayat', label: 'Block Panchayat' },
];

const LocalBodyWise = React.memo(() => {
  const [localBodyType, setLocalBodyType] = useState('gramapanchayat');
  const [localBodyContent, setLocalBodyContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [electionData, setElectionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [localBodies, setLocalBodies] = useState([]);
  const [selectedLocalBody, setSelectedLocalBody] = useState('');
  const [activeTab, setActiveTab] = useState('2025');
  const [shareState, setShareState] = useState(null);
  const [resetAutoComplete, setResetAutoComplete] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  const selectedLocalBodyRef = useRef(null);

  const handleShare = async () => {
    const result = await shareComponent('Statewide Results', `${window.location.origin}${window.location.pathname}?show=localbodies`);
    setShareState(result);
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!localBodyType) return;

      try {
        let endpoint;
        switch (localBodyType) {
          case 'corporation':
            endpoint = ENDPOINTS.CORP_TEXT;
            break;
          case 'jillapanchayat':
            endpoint = ENDPOINTS.JILA_TEXT;
            break;
          default:
            endpoint = ENDPOINTS.DISTRICTS_TEXT;
        }

        const response = await fetch(endpoint, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) throw new Error('Failed to load content');

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const options = Array.from(doc.querySelectorAll('option'));

        const contentItems = options
          .filter(option => {
            const value = option.value?.trim().toLowerCase() || '';
            return value && !value.includes('select');
          })
          .map(option => ({
            value: option.value,
            label: option.textContent
            // label: option.textContent.toLowerCase()==='trivandrum'?'Thiruvananthapuram':option.textContent
          }));

        setLocalBodyContent(contentItems);
        if (contentItems.length > 0 && !selectedContent) {
          setSelectedContent(contentItems[0].value);
        }
      } catch (error) {
        console.error('Error loading content:', error);
        setLocalBodyContent([]);
        setSelectedContent('');
      }
    };

    fetchContent().then(() => {
      setIsLoading(false);
    });
  }, [localBodyType, selectedContent]);

  const fetchLocalBodies = useCallback(async (localBodyType, district) => {
    try {
      let endpoint;
      switch (localBodyType) {
        case 'municipality':
          endpoint = ENDPOINTS.MUNICIPALITY_TEXT(district);
          break;
        case 'blockpanchayat':
          endpoint = ENDPOINTS.BLOCK_PANCHAYATH_TEXT(district);
          break;
        case 'gramapanchayat':
          endpoint = ENDPOINTS.GRAMA_PANCHAYATH_TEXT(district);
          break;
        default:
          return [];
      }

      const response = await fetch(endpoint, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) throw new Error('Failed to load local bodies');

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const options = Array.from(doc.querySelectorAll('option'));

      return options
        .filter(option => {
          const value = option.value?.trim().toLowerCase() || '';
          return value && !value.includes('select');
        })
        .map(option => ({
          value: option.value,
          label: option.textContent
        }));
    } catch (error) {
      console.error('Error loading local bodies:', error);
      return [];
    }
  }, []);

  const loadAndSetLocalBodies = useCallback(async (type, district, selectedId = null) => {
    if (!district || !['municipality', 'blockpanchayat', 'gramapanchayat'].includes(type)) {
      setLocalBodies([]);
      setSelectedLocalBody('');
      return;
    }
    const bodies = await fetchLocalBodies(type, district);
    setLocalBodies(bodies);
    if (selectedId) {
      setSelectedLocalBody(selectedId);
    } else {
      if (initialLoad) {
        setSelectedLocalBody(bodies.length > 0 ? bodies[0].value : '');
        setInitialLoad(false);
      }
    }
  }, [fetchLocalBodies, initialLoad]);

  const fetchElectionData = useCallback(async (id = null) => {
    let lb_id = id;

    if (!lb_id) {
      if (['municipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType)) {
        lb_id = selectedLocalBody;
      } else {
        lb_id = selectedContent;
      }
    }

    if (!lb_id) {
      // console.warn('No valid local body ID provided');
      setElectionData(null);
      return;
    }

    setIsLoading(true);

    try {
      const now = new Date();
      const timestamp = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

      const endpointTemplate = activeTab === '2025'
        ? ENDPOINTS.ELECTION_DATA_2025(lb_id)
        : ENDPOINTS.ELECTION_DATA_2020(lb_id);

      if (!endpointTemplate) {
        throw new Error('Invalid endpoint template');
      }

      const response = await fetch(`${endpointTemplate}?ver=${timestamp}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      setElectionData(data);
    } catch (err) {
      console.error('Error fetching election data:', err);
      setElectionData(null);
    } finally {
      setIsLoading(false);
    }
  }, [localBodyType, selectedLocalBody, selectedContent, activeTab]);

  const handleLocalBodySelect = async (selectedValue) => {
    if (!selectedValue) return;

    setResetAutoComplete(prev => prev + 1);

    if (typeof selectedValue === 'string') {
      setLocalBodyType(selectedValue);
      setSelectedContent('');
      setSelectedLocalBody('');

      if (['corporation', 'jillapanchayat'].includes(selectedValue)) {
        setSelectedDistrict('');
        setElectionData(null)
      }
      return;
    }

    // Handle case when a full suggestion object is passed (from autocomplete)
    const componentLocalBodyType = selectedValue.localBodyType?.toLowerCase() || '';
    if (!componentLocalBodyType) {
      console.error('No local body type found in the selected suggestion');
      return;
    }

    try {
      const componentLocalBodyType = selectedValue.localBodyType?.toLowerCase() || '';
      const componentDistrict = selectedValue.district?.toLowerCase() || '';

      setLocalBodyType(componentLocalBodyType);
      setSelectedContent(selectedValue.id || selectedValue.value);

      if (['corporation', 'jillapanchayat'].includes(componentLocalBodyType)) {
        setSelectedDistrict('');
        setSelectedLocalBody('');
        setLocalBodies([]);
      } else if (['municipality', 'blockpanchayat', 'gramapanchayat'].includes(componentLocalBodyType)) {
        if (selectedValue.district) {
          // First set the district
          setSelectedDistrict(componentDistrict);

          // Then load the local bodies
          const bodies = await fetchLocalBodies(componentLocalBodyType, componentDistrict);

          // Update the local bodies state
          setLocalBodies(bodies);

          // Find the matching body - make sure to compare the right properties
          const matchingBody = bodies.find(body =>
            (body.id && body.id.toString() === selectedValue.id.toString()) ||
            (body.value && body.value.toString() === selectedValue.id.toString())
          );

          if (matchingBody) {
            // Use a small timeout to ensure state updates have propagated
            setTimeout(() => {
              const valueToSet = matchingBody.id || matchingBody.value;
              // console.log('Setting selectedLocalBody to:', valueToSet);
              setSelectedLocalBody(valueToSet);

              // Debug: Log the current state after a small delay
              setTimeout(() => {
                // console.log('Current selectedLocalBody after update:', selectedLocalBody);
              }, 100);
            }, 0);
          }
        }
      }

      await fetchElectionData(selectedValue.id || selectedValue.value);

      /*      if (['corporation', 'jillapanchayat'].includes(componentLocalBodyType)) {
              setSelectedContent(selectedValue.id || selectedValue.value);
              setSelectedLocalBody('');
              setSelectedDistrict('');
              await fetchElectionData(selectedValue.id || selectedValue.value);
            } else {
              const district = selectedValue.district?.toLowerCase() || '';
              if (!district) {
                console.warn('No district provided for the selected local body');
                return;
              }
              setSelectedDistrict(district);
              await new Promise(resolve => setTimeout(resolve, 0));
              const bodies = await fetchLocalBodies(componentLocalBodyType, district);
              setLocalBodies(bodies);
              await new Promise(resolve => setTimeout(resolve, 0));
              if (selectedValue.id) {
                setSelectedLocalBody(selectedValue.id);
                await fetchElectionData(selectedValue.id);
              }
            }*/
    } catch (error) {
      console.error('Error handling local body selection:', error);
    }
  };

  useEffect(() => {
    if (selectedLocalBodyRef.current) {
      const fetchData = async () => {
        await fetchElectionData(selectedLocalBodyRef.current.id);
        selectedLocalBodyRef.current = null;
      };
      fetchData();
    }
  }, [localBodyType, selectedLocalBody, selectedDistrict, fetchElectionData]);

  const handleContentSelect = useCallback(async (content) => {
    setSelectedContent(content);
    // Clear local body selection when content changes
    setSelectedLocalBody('');
    // If you need to fetch new data immediately:
    await fetchElectionData(content);
  }, [fetchElectionData]);

  useEffect(() => {
    const loadData = async () => {
      if (!selectedContent && !selectedLocalBody) return;
      setIsLoading(true);
      try {
        await fetchElectionData();
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [localBodyType, selectedLocalBody, selectedContent, activeTab, fetchElectionData]);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetch(ENDPOINTS.DISTRICTS_TEXT, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.ok) throw new Error('Failed to load districts');

        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const options = Array.from(doc.querySelectorAll('option'));

        const districtItems = options
          .filter(option => option.value && option.value.trim().toLowerCase() !== 'select district')
          .map(option => ({
            value: option.value,
            label: option.textContent
          }));

        setDistricts(districtItems);
        if (districtItems.length > 0) {
          setSelectedDistrict(districtItems[0].value);
        }
      } catch (error) {
        console.error('Error loading districts:', error);
        setDistricts([]);
      }
    };

    fetchDistricts();
  }, []);

  useEffect(() => {
    loadAndSetLocalBodies(localBodyType, selectedDistrict);
  }, [localBodyType, selectedDistrict, loadAndSetLocalBodies]);

  /*  const localBodyTypeLabel = useMemo(() => {
      return LOCAL_BODY_TYPES.find(lb => lb.value === localBodyType)?.label || 'Select Local Body type';
    }, [localBodyType]);*/

  const districtLabel = useMemo(() => {
    return districts.find(d => d.value === selectedDistrict)?.label || 'Select Districts';
  }, [districts, selectedDistrict]);

  const renderBarChart = (data) => {
    if (!data) return null;

    // Create an array of categories with their corresponding values
    const categoriesData = [
      { name: 'LDF', value: parseInt(data.ldf || 0) },
      { name: 'UDF', value: parseInt(data.udf || 0) },
      { name: 'NDA', value: parseInt(data.nda || 0) },
      { name: 'OTH', value: parseInt(data.oth || 0) }
    ];
    // Sort the categories by value in descending order
    const sortedCategories = [...categoriesData].sort((a, b) => b.value - a.value);
    // Extract the sorted category names
    const sortedCategoryNames = sortedCategories.map(item => item.name);
    // Create the series data in the same order as sorted categories
    const seriesData = sortedCategories.map(item => ({
      name: item.name,
      y: item.value,
      color:
        item.name === 'LDF' ? '#ED423D' :
          item.name === 'UDF' ? '#5C93C7' :
            item.name === 'NDA' ? '#FC9E35' : '#787878'
    }));

    const chartOptions = {
      chart: {
        type: 'column',
        height: '230',
        style: {
          fontFamily: "'Roboto', Arial, sans-serif"
        }
      },
      legend: {
        enabled: false
      },
      title: {
        text: ''
      },
      xAxis: {
        // categories: ['LDF', 'UDF', 'NDA', 'OTH'],
        categories: sortedCategoryNames,
        crosshair: true
      },
      yAxis: {
        min: 0,
        title: {
          text: ''
        },
        labels: { enabled: false },
        gridLineColor: 'rgba(0, 0, 0, 0.04)'
      },
      tooltip: {
        headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
        pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
          '<td style="padding:0"><b>{point.y}</b></td></tr>',
        footerFormat: '</table>',
        shared: true,
        useHTML: true
      },
      plotOptions: {
        column: {
          pointWidth: 45,
          pointPadding: 0.2,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            format: '{point.y}',
            filter: {
              property: 'y',
              operator: '>',
              value: 0
            },
            style: {
              color: '#000000',
              textOutline: 'none',
              fontWeight: '500',
              fontSize: '12px'
            }
          }
        }
      },
      series: [{
        name: 'Seats',
        data: seriesData
        // data: [{
        //   name: 'LDF',
        //   y: parseInt(data.ldf || 0),
        //   color: '#ED423D'
        // }, {
        //   name: 'UDF',
        //   y: parseInt(data.udf || 0),
        //   color: '#5C93C7'
        // }, {
        //   name: 'NDA',
        //   y: parseInt(data.nda || 0),
        //   color: '#FC9E35'
        // }, {
        //   name: 'Others',
        //   y: parseInt(data.oth || 0),
        //   color: '#787878'
        // }]
      }],
      credits: {
        enabled: false
      }
    };

    return (
      <div>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
        />
      </div>
    );
  };

  useEffect(() => {
    if (selectedLocalBody && localBodies.length > 0) {
      const selected = localBodies.find(lb => lb.id === selectedLocalBody);
      if (selected) {
        fetchElectionData(selectedLocalBody);
      }
    }
  }, [localBodies, selectedLocalBody, fetchElectionData]);

  const handleWardWise = () => {
    const url = `https://results.mathrubhumi.com/result/localbody_ward_wise/index.html?local=${selectedLocalBody || selectedContent}`;
    openPopup(url, 900, 900);
  };

  const showControls = !getUrlParam('show');

  return (
    <>
      {!showControls && (
        <div className="allianceHeader">LOCALBODY-WISE RESULTS: KERALA PANCHAYAT ELECTIONS 2025</div>
      )}
      <div className="local-body-container" id="localBodyWise">
        <div className="local-body-selector">
          {/*<select
            id="localBodyType"
            name="localBodyType"
            className="form-select"
            value={localBodyType}
            onChange={(e) => handleLocalBodySelect(e.target.value)}
            aria-label="Select local body type"
          >
            {LOCAL_BODY_TYPES.map((localBody) => (
              <option key={localBody.value} value={localBody.value}>
                {localBody.label}
              </option>
            ))}
          </select>*/}

          <select
            id="localBodyType"
            name="localBodyType"
            className="form-select"
            value={localBodyType}
            onChange={(e) => handleLocalBodySelect(e.target.value)}
            aria-label="Select local body type"
          >
            {/*<option value="" disabled>{localBodyTypeLabel}</option>*/}
            {LOCAL_BODY_TYPES.map((localBody) => (
              <option key={localBody.value} value={localBody.value}>
                {localBody.label}
              </option>
            ))}
          </select>

          <div className="select-wrapper select-wrapper-1">
            <select
              id="districts"
              name="districts"
              className="form-select"
              value={['corporation', 'jillapanchayat'].includes(localBodyType) ? selectedContent : (selectedDistrict || '')}
              onChange={(e) => {
                if (['corporation', 'jillapanchayat'].includes(localBodyType)) {
                  handleContentSelect(e.target.value);
                } else {
                  setSelectedDistrict(e.target.value);
                }
              }}
              disabled={['corporation', 'jillapanchayat'].includes(localBodyType) ? localBodyContent.length === 0 : districts.length === 0}
            >
              {['corporation', 'jillapanchayat'].includes(localBodyType) ? (
                localBodyContent.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))
              ) : (
                <>
                  <option value="" disabled>{districtLabel}</option>
                  {districts.map((district) => (
                    <option key={district.value} value={district.value}>
                      {district.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        </div>

        {['municipality', 'blockpanchayat', 'gramapanchayat'].includes(localBodyType) && selectedDistrict && (
          <div className="select-wrapper select-wrapper-2">
            <select
              id="localBodies"
              name="localBodies"
              className="form-select form-select-2"
              value={selectedLocalBody}
              onChange={(e) => setSelectedLocalBody(e.target.value)}
              disabled={localBodies.length === 0}
            >
              <option value="">Select {localBodyType}</option>
              {localBodies.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="election-results-inner local-body-wise">
          <div className="icons">
            {showControls && (
              <MenuButton
                menuItems={[
                  {
                    label: 'FULL RESULT',
                    onClick: () => openPopup('https://results.mathrubhumi.com/result/panchayat-2025/full-result.html', 800)
                  },
                  {
                    label: 'MAP VIEW',
                    onClick: () => openPopup('https://public.flourish.studio/visualisation/4658473/', 1000)
                  },
                  {
                    label: 'PARTY-WISE',
                    onClick: () => openPopup(`${window.location.origin}${window.location.pathname}?show=parties`, 700)
                  }
                ]}
              >
                <svg className="menu-icon" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path
                    d="M2.83331 4.25H14.1666M2.83331 8.5H14.1666M2.83331 12.75H14.1666"
                    stroke="#403E3E"
                    strokeWidth="1.41667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </MenuButton>
            )}
            <div className="icons-r">
              <AutoComplete
                onSelectLocalBody={handleLocalBodySelect}
                resetTrigger={resetAutoComplete}
              />
              {showControls && (
                <svg
                  className="open-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  onClick={(e) => {
                    e.preventDefault();
                    openPopup(`${window.location.origin}${window.location.pathname}?show=localbodies`, 525, 900);
                  }}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  aria-label="Open local body wise in new tab"
                >
                  <path d="M0 4.85658C0 5.30531 0.363772 5.66908 0.8125 5.66908C1.26123 5.66908 1.625 5.30531 1.625 4.85658H0ZM1.625 0.8125C1.625 0.363772 1.26123 0 0.8125 0C0.363772 0 0 0.363772 0 0.8125H1.625ZM0.8125 0C0.363772 0 0 0.363772 0 0.8125C0 1.26123 0.363772 1.625 0.8125 1.625V0ZM4.85658 1.625C5.30531 1.625 5.66908 1.26123 5.66908 0.8125C5.66908 0.363772 5.30531 0 4.85658 0V1.625ZM1.38702 0.237976C1.06973 -0.0793219 0.555273 -0.0793219 0.237976 0.237976C-0.0793219 0.555273 -0.0793219 1.06973 0.237976 1.38702L1.38702 0.237976ZM5.65464 6.80366C5.97198 7.12097 6.48635 7.12097 6.80366 6.80366C7.12097 6.48635 7.12097 5.97198 6.80366 5.65464L5.65464 6.80366ZM11.9351 15.1667C11.4864 15.1667 11.1226 15.5304 11.1226 15.9792C11.1226 16.4279 11.4864 16.7917 11.9351 16.7917V15.1667ZM15.9792 16.7917C16.4279 16.7917 16.7917 16.4279 16.7917 15.9792C16.7917 15.5304 16.4279 15.1667 15.9792 15.1667V16.7917ZM15.1667 15.9792C15.1667 16.4279 15.5304 16.7917 15.9792 16.7917C16.4279 16.7917 16.7917 16.4279 16.7917 15.9792H15.1667ZM16.7917 11.9351C16.7917 11.4864 16.4279 11.1226 15.9792 11.1226C15.5304 11.1226 15.1667 11.4864 15.1667 11.9351H16.7917ZM15.4047 16.5537C15.722 16.871 16.2363 16.871 16.5537 16.5537C16.871 16.2363 16.871 15.722 16.5537 15.4047L15.4047 16.5537ZM11.137 9.98801C10.8197 9.6707 10.3053 9.6707 9.98801 9.98801C9.6707 10.3053 9.6707 10.8197 9.98801 11.137L11.137 9.98801ZM1.625 11.9351C1.625 11.4864 1.26123 11.1226 0.8125 11.1226C0.363772 11.1226 0 11.4864 0 11.9351H1.625ZM0 15.9792C0 16.4279 0.363772 16.7917 0.8125 16.7917C1.26123 16.7917 1.625 16.4279 1.625 15.9792H0ZM0.8125 15.1667C0.363772 15.1667 0 15.5304 0 15.9792C0 16.4279 0.363772 16.7917 0.8125 16.7917V15.1667ZM4.85658 16.7917C5.30531 16.7917 5.66908 16.4279 5.66908 15.9792C5.66908 15.5304 5.30531 15.1667 4.85658 15.1667V16.7917ZM0.237976 15.4047C-0.0793219 15.722 -0.0793219 16.2363 0.237976 16.5537C0.555273 16.871 1.06973 16.871 1.38702 16.5537L0.237976 15.4047ZM6.80366 11.137C7.12097 10.8197 7.12097 10.3053 6.80366 9.98801C6.48635 9.6707 5.97198 9.6707 5.65464 9.98801L6.80366 11.137ZM11.9351 0C11.4864 0 11.1226 0.363772 11.1226 0.8125C11.1226 1.26123 11.4864 1.625 11.9351 1.625V0ZM15.9792 1.625C16.4279 1.625 16.7917 1.26123 16.7917 0.8125C16.7917 0.363772 16.4279 0 15.9792 0V1.625ZM16.7917 0.8125C16.7917 0.363772 16.4279 0 15.9792 0C15.5304 0 15.1667 0.363772 15.1667 0.8125H16.7917ZM15.1667 4.85658C15.1667 5.30531 15.5304 5.66908 15.9792 5.66908C16.4279 5.66908 16.7917 5.30531 16.7917 4.85658H15.1667ZM16.5537 1.38702C16.871 1.06973 16.871 0.555273 16.5537 0.237976C16.2363 -0.0793219 15.722 -0.0793219 15.4047 0.237976L16.5537 1.38702ZM9.98801 5.65464C9.6707 5.97198 9.6707 6.48635 9.98801 6.80366C10.3053 7.12097 10.8197 7.12097 11.137 6.80366L9.98801 5.65464ZM1.625 4.85658V0.8125H0V4.85658H1.625ZM0.8125 1.625H4.85658V0H0.8125V1.625ZM0.237976 1.38702L5.65464 6.80366L6.80366 5.65464L1.38702 0.237976L0.237976 1.38702ZM11.9351 16.7917H15.9792V15.1667H11.9351V16.7917ZM16.7917 15.9792V11.9351H15.1667V15.9792H16.7917ZM16.5537 15.4047L11.137 9.98801L9.98801 11.137L15.4047 16.5537L16.5537 15.4047ZM0 11.9351V15.9792H1.625V11.9351H0ZM0.8125 16.7917H4.85658V15.1667H0.8125V16.7917ZM1.38702 16.5537L6.80366 11.137L5.65464 9.98801L0.237976 15.4047L1.38702 16.5537ZM11.9351 1.625H15.9792V0H11.9351V1.625ZM15.1667 0.8125V4.85658H16.7917V0.8125H15.1667ZM15.4047 0.237976L9.98801 5.65464L11.137 6.80366L16.5537 1.38702L15.4047 0.237976Z" fill="#403E3E"/>
                </svg>
              )}
              <svg
                className="share-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                onClick={handleShare}
                style={{ cursor: 'pointer' }}
                role="button"
                aria-label="Share Local Body-wise Results"
              >
                <g clipPath="url(#clip0_644_1392)">
                  <path fillRule="evenodd" clipRule="evenodd" d="M14.7371 0.599674C13.8626 -0.547357 12.007 0.0610694 12.007 1.49485V2.93454C9.17783 2.90105 7.24249 3.87355 5.95335 5.23165C4.57526 6.68347 4.00844 8.49855 3.77091 9.73965C3.65216 10.3601 4.05917 10.8103 4.48693 10.9658C4.89557 11.1143 5.44162 11.0507 5.80602 10.6342C6.84027 9.45202 8.84963 7.8021 12.007 7.9098V9.75517C12.007 11.1889 13.8626 11.7973 14.7371 10.6503L17.545 6.96775C18.1517 6.17205 18.1517 5.07795 17.545 4.28224L14.7371 0.599674ZM5.61151 8.70712C6.96541 7.52557 8.90888 6.41676 12.007 6.41676H12.7655C13.1844 6.41676 13.5239 6.75077 13.5239 7.16278L13.5238 9.75517L16.3316 6.07258C16.5338 5.80735 16.5338 5.44265 16.3316 5.17741L13.5238 1.49485V3.68053C13.5238 4.09252 13.1843 4.42651 12.7654 4.42651H12.007C8.59448 4.42651 7.06197 6.24985 7.06197 6.24985C6.35643 6.99313 5.90238 7.8765 5.61151 8.70712Z" fill="#403E3E"/>
                  <path d="M5.25 0.75293H3C1.75736 0.75293 0.75 1.76028 0.75 3.00293V15.0029C0.75 16.2456 1.75736 17.2529 3 17.2529H15C16.2427 17.2529 17.25 16.2456 17.25 15.0029V12.7529C17.25 12.3387 16.9142 12.0029 16.5 12.0029C16.0858 12.0029 15.75 12.3387 15.75 12.7529V15.0029C15.75 15.4171 15.4142 15.7529 15 15.7529H3C2.58579 15.7529 2.25 15.4171 2.25 15.0029V3.00293C2.25 2.58871 2.58579 2.25293 3 2.25293H5.25C5.66421 2.25293 6 1.91714 6 1.50293C6 1.08871 5.66421 0.75293 5.25 0.75293Z" fill="#403E3E"/>
                </g>
                <defs>
                  <clipPath id="clip0_644_1392)">
                    <rect width="18" height="18" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
          </div>

          {isLoading ? (
            <LoadingSpinner />
          ) : electionData ? (
            <div className="election-data">
              <h3 className={electionData.front.lead.toLowerCase()}><span>{LOCAL_BODY_TYPES.find(type => type.value === localBodyType)?.label || localBodyType}: </span> {electionData.local_body} ({electionData.total_seats} WARDS)</h3>
              <div className="tabs">
                <ul>
                  <li
                    className={activeTab === '2025' ? 'active' : ''}
                    onClick={() => setActiveTab('2025')}
                  >
                    2025
                  </li>
                  <li
                    className={activeTab === '2020' ? 'active' : ''}
                    onClick={() => setActiveTab('2020')}
                  >
                    2020 *
                  </li>
                </ul>
              </div>
              <div className="tab-container tab-container-lb">
                <div className={`lb-lead-stat ${electionData.status.toLowerCase() !== 'hung' ? electionData.front.lead.toLowerCase() : ''}`}>
                  {electionData.status.toLowerCase() !== 'hung' ? electionData.front.lead : ''}
                  {['leading', 'wins'].includes(electionData.status.toLowerCase()) ? ' WON' : ' ' + electionData.status}
                </div>
                <div className="tab-content">
                  {renderBarChart(electionData.front)}
                  <div className="lb-result-main">
                    <div className="lb-result">
                      {Object.entries(electionData.front)
                        .filter(([key]) => ['ldf', 'udf', 'nda', 'oth'].includes(key))
                        .map(([key, value]) => ({ key, value }))
                        .sort((a, b) => b.value - a.value)
                        .map(({ key, value }, index, array) => {
                          const isLeading = index === 0 && value > 0;
                          return (
                            <div key={key} className={`lb-result-in ${isLeading ? 'flipping-text-Y flipping-text' : ''}`}>
                              <span className={electionData.front.lead.toLowerCase() === key ? key : 'oth'}>{value}</span>
                              <span className={key}>{key.toUpperCase()}</span>
                            </div>
                          );
                        })}
                    </div>
                    <div className="prev-stat">
                      {activeTab === '2025' ? '2020' : '2015'}
                      <svg width="6" height="7" viewBox="0 0 6 7"><path d="M6 3.46411L-3.26266e-07 6.92821L-2.34249e-08 9.51289e-06L6 3.46411Z" fill="#787878"/></svg>
                      {Object.entries(electionData.front)
                        .filter(([key]) => ['ldf', 'udf', 'nda', 'oth'].includes(key))
                        .map(([key, value]) => ({ key, value }))
                        .sort((a, b) => b.value - a.value)
                        .map(({ key }, i, array) => {
                          const year = activeTab === '2025' ? '2020' : '2015';
                          const leadKey = `lead_${year}`;
                          const valueKey = `${key}_${year}`;
                          return (
                            <span key={key} className={electionData.front[leadKey]?.toLowerCase() === key ? key : 'oth'}>
                              {key.toUpperCase()} {electionData.front?.[valueKey]}{i < array.length - 1 ? ' | ' : ''}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <ActionButtons
              showWards={true}
              onWardWise={handleWardWise}
          />
        </div>
      </div>
      {shareState?.showModal && (
        <ShareModal
          isOpen={shareState.showModal}
          onClose={() => setShareState(null)}
          shareLinks={shareState.shareLinks}
          shareText={shareState.shareText}
          url={shareState.url}
        />
      )}
    </>
  );
});

export default LocalBodyWise;